import { Component, OnInit, ViewEncapsulation, Input, Output, OnChanges, SimpleChanges, ViewChild, QueryList, ViewChildren, EventEmitter } from '@angular/core';
import { MediaFile, MediaFileType, MediaType, AllowedMediaMap, Media, GroupState, ImageFormatMap, MotesPaths, ContainerType, fileToDataUrl, ContainerOptions } from '../../models';
import { UploadManagerService } from '../upload-manager/upload-manager.service';
import { AutoSubOnDestroy, AutoSub, MediaService, ToastService, ToastType } from '../../providers';
import { UploadCropperComponent } from '../upload-cropper/upload-cropper.component';
import { UploadFileComponent } from '../upload-file/upload-file.component';
import { saveAs } from 'file-saver';

import { MediaUploadEvent, MediaUploadEventType, UploadMediaService } from './upload-media.service';
import { filter, first } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MotesLoginService } from '@motes-sdk/motes-login';
import { Animations } from '../../../src/animations';

@AutoSubOnDestroy()
@Component({
    selector: 'upload-media',
    templateUrl: './upload-media.component.html',
    styleUrls: ['./upload-media.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeInOut(200, 200)],
})
export class UploadMediaComponent implements OnInit, OnChanges {

    @ViewChild('uploadCropper', { static: true }) uploadCropper: UploadCropperComponent;
    @Input('externalCropper') externalCropper: UploadCropperComponent;

    // Context of where the media is used (mote, profile, world, etc.)
    @Input() containerOptions: ContainerOptions;

    // Allowed types of media to be uploaded
    @Input() allowedMediaTypes: AllowedMediaMap = { IMAGE: true, VIDEO: true, SOUND: true, AMBISONIC: true, YOUTUBE_VIDEO: true, FILMCENTRALEN_VIDEO: true };
    // Allows to set a fixed active file - other files than the selected will never be shown
    // In case no fixed active file is set, the component will select/show the first file that matched media type / allowed media types
    @Input() fixedActiveFile: MediaFileType = null;
    // State control variables
    @Input() isHovering: boolean = false;

    @Input() selectFromLibraryEnabled: boolean = false;

    // Default image size, ratio and format of files in media object
    @Input() imageFormats: ImageFormatMap = UploadMediaService.DefaultImageFormat;

    // Make dotted cropping frame circular
    @Input() roundCropper: boolean = false;

    // Disable crop and delete button
    @Input() disableEditing: boolean = false;

    @Input() enableMediaUnlink: boolean = false;

    // File to be uploaded and manipulate
    @Input('media') set setMedia(media: Media) {
        // Init media object upload paths
        this.initMedia(media);
        this.initMetaDataModels();
    }

    // Media element shown in view
    public media: Media;

    // Hide or show media file tabs
    @Input() showFiles: boolean = true;
    @Input() readOnlyEnabled: boolean = false;
    // Fix uploader aspect ratio to 16/9
    @Input() fixedAspectRatio: boolean = false;
    // Fix aspect ratio of empty uploader to 16/9
    @Input() fixedEmptyAspectRatio: boolean = true;
    // Show placeholder illustrating allowed media types
    @Input() showPlaceholder: boolean = true;
    // Show preview (if available) on video media
    @Input() showVideoCover: boolean = true;

    @Input() setVideoImagePlaceholder: boolean = true;

    @Output() mediaUploadPending = new EventEmitter<Media>();
    @Output() mediaUploadDone = new EventEmitter<Media>();
    @Output() mediaCroppingDone = new EventEmitter<Media>();
    @Output() copyMediaRequest = new EventEmitter<Media>();
    @Output() openLibraryRequest = new EventEmitter<void>();
    @Output() mediaDeleted = new EventEmitter<Media>();

    @ViewChildren(UploadFileComponent) uploadComponents: QueryList<UploadFileComponent>;

    public groupProgress: GroupState = { status: 'Idle', totalBytes: 0, totalPct: 0 };
    public imageFiles: MediaFile[] = [];
    public shownImageFiles: MediaFile[] = [];
    public mediaMoteLinks: string[] = [];
    public showDeleteOverlay: boolean = false;
    public deleteOverlayTitle: string = '';

    // Storage of media with updates from database to be shown when upload progress is done
    private delayedMediaUpdate: Media;

    // View state
    public activeFile: MediaFile = null;
    public croppingModeEnabled = false;
    public uploadError: string = "";
    public showMetaDataOverlay: boolean = false;
    public isUploadInProgress: boolean = false;
    public isCroppingInProgress: boolean = false;
    public isVideoEditEnabled: boolean = false;
    public videoPlayerTimeInMs: number = 0;

    // Expose types to view
    public MediaType = MediaType;
    public MediaFileType = MediaFileType;
    public FileIcons = Media.FileIcons;
    public externalVideoPopupSubject: Subject<void> = new Subject<void>();
    public MediaTypeLabel: { [type in MediaType]: string } = {
        'UNDEFINED': '',
        'IMAGE': $localize`image`,
        'VIDEO': $localize`video`,
        'YOUTUBE_VIDEO': $localize`video`,
        'FILMCENTRALEN_VIDEO': $localize`video`,
        'SOUND': $localize`audio`,
        'AMBISONIC': $localize`audio`
    };

    public metaDescriptionModel: string = '';
    public metaCopyrightModel: string = '';

    constructor(private mediaService: MediaService,
        private uploadMediaService: UploadMediaService,
        private uploadManagerService: UploadManagerService,
        private httpClient: HttpClient,
        private toastService: ToastService,
        private loginService: MotesLoginService,
    ) { }

    ngOnInit() {
        // Watch upload progress
        AutoSub(this).reg['progressSub'] = this.uploadManagerService.watchUploadGroupProgress().subscribe((progressMap) => {
            this.updateMediaProgress(this.media);
        });

        // Watch upload errors
        AutoSub(this).reg['uploadErrSub'] = this.uploadManagerService.watchUploadErrors().subscribe((ev) => {
            console.warn("Upload error ev", ev);
            this.uploadError = ev.error.message;
            this.groupProgress.status = 'Error';
            this.groupProgress.totalPct = 0;
            this.toastService.addToast($localize`Could not upload media, please try again or use another file.`, ToastType.ERR);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Make sure correct active file is chosen, no matter in which order the inputs of the component are set
        if(changes.allowedMediaTypes || changes.fixedActiveFile || changes.imageFormats) {
            this.activateFirstFileTab();
        }

        // Make sure correct storage path is chosen, no matter in which order the inputs of the component are set
        if(changes.containerOptions) {
            this.updateStoragePath();
        }
    }

    private async updateMediaProgress(media: Media, restoreUploadBlob: boolean = false) : Promise<void> {
        const progressMap = await this.uploadManagerService.watchUploadGroupProgress().pipe(first()).toPromise();
        this.groupProgress = progressMap[media.key] || { status: 'Idle', totalBytes: 0, totalPct: 0 };

        // Clear resourceList so that no resources are accumulated on each upload
        if(this.groupProgress.status == 'Cached') {
            this.groupProgress.resourceList = [];
            return;
        }

        if(!media) return;

        // Restore blob from upload context when changing between images during upload
        if(restoreUploadBlob && !this.media.original.url && this.groupProgress.resourceList?.length > 0) {
            // Unfortunately we need to wait ~100 ms here to be sure the lastVal and the context has propagated from the upload file service
            await new Promise<void>((resolve) => setTimeout(() => resolve(), 100));
            // Try to get the upload file blob from upload service context
            const uploadContext: MediaFile = this.groupProgress.resourceList[0]?.lastVal?.context;
            // Do nothing if the upload in progress is not original image blob
            if(!uploadContext || !uploadContext.blob || uploadContext.type != MediaFileType.ORIGINAL) return;
            // Temporary set the origin url to be the file currently uploading
            this.media.original.url = await fileToDataUrl(uploadContext.blob);
            this.activeFile = this.media.original;
        }
    }

    private generateStoragePath(): string {
        // Skip generating storage path until container options are set
        if(!this.containerOptions) return;

        switch(this.containerOptions.type) {
            case (ContainerType.MOTE): {
                return MotesPaths.MOTE_MEDIA_HD_PATH(this.media.project);
            }
            case (ContainerType.PROFILE): {
                const userUid = this.loginService.userRights.uid;
                return MotesPaths.USER_PUBLIC_MEDIA_HD_PATH(userUid);
            }
            case (ContainerType.WORLD): {
                return MotesPaths.WORLD_MEDIA_HD_PATH(this.media.project);
            }
            case (ContainerType.PRODUCT): {
                return MotesPaths.PRODUCT_HD_PATH(this.media.project, this.containerOptions.containerKey);
            }
            case (ContainerType.FEATURED_TOURS): {
                return MotesPaths.FEATURED_MEDIA_DB_PATH(this.containerOptions.containerKey, this.containerOptions.tourVariant);
            }
            case (ContainerType.BLOG_POST): {
                return MotesPaths.POST_HD_PATH();
            }
            default: {
            }
        }
    }

    private updateStoragePath() {
        if(!this.media) return;
        // Init or re-init storage path
        const storagePath = this.generateStoragePath();
        this.media.initStoragePaths(storagePath, false);
    }

    private initMedia(media: Media) {
        // Skip media update if upload is in progress of this media
        // However update media in the case upload is in progress and media key changed (user changed story)
        if((this.isUploadInProgress || this.isCroppingInProgress) && media?.key == this.media.key) {
            this.delayedMediaUpdate = media;
            return;
        }

        // Update the state of upload progress when new media object is selected
        if(this.media?.key != media?.key) {
            this.updateMediaProgress(media, true);
            this.isUploadInProgress = Boolean(this.groupProgress.totalPct > 0 && this.groupProgress.totalPct < 100);
            // TODO: Make cropping part of the group progress to avoid resetting this when cropping is not done
            this.isCroppingInProgress = false;
        }

        this.media = media;

        // Generate a new media element
        if(!this.media) {
            this.media = new Media(null);
            this.media.key = this.mediaService.generateKey();
        }

        if(!this.media.key) {
            // console.warn("Generate new media key as provided media has no key");
            this.media.key = this.mediaService.generateKey();
        }

        this.updateImageList();
        this.activateFirstFileTab();

        AutoSub(this).reg['groupResultSub'] = this.uploadManagerService.watchUploadGroupResult(this.media.key).subscribe(ev => {
            // Notify parent that all files in this media was successfully uploaded
            (this.croppingModeEnabled) ? this.mediaUploadPending.emit(this.media) : this.mediaUploadDone.emit(this.media);
        });

        // Watch media upload state events
        let watchUploadEv = this.uploadMediaService.watchUploadEvents().pipe(filter(ev => ev.media.key == this.media.key));
        AutoSub(this).reg['mediaUploadSub'] = watchUploadEv.subscribe(ev => {
            this.handleUploadEvent(ev);
        });

        AutoSub(this).reg['mediaLink'] = this.mediaService.watchMediaLink(this.media.key).subscribe(mediaLinkMap => {
            this.mediaMoteLinks = Object.values(mediaLinkMap || {}).map(link => link.containerKey);
        });
    }

    public initMetaDataModels() {
        this.metaDescriptionModel = this.media.description;
        this.metaCopyrightModel = this.media.copyright;
    }

    private activateFirstFileTab() {
        // Select the 'fixed' active file if one is configured for this uploads component
        if(this.fixedActiveFile) {
            this.activeFile = this.media[this.fixedActiveFile];
            return;
        }

        // Try to find a file that matches the media type which is allowed
        this.activeFile = this.media.files.find((file) => {
            // Select file matching media type ies SOUND = SOUND, VIDEO = VIDEO
            const fileTypeMatch = (file.mediaType == this.media.type);
            // Check that the media type is allowed in this upload component
            const isAllowed = Boolean(this.allowedMediaTypes[file.mediaType]);
            // Check that image file is defined in image formats
            const isInFormatList = Boolean(this.media.type == MediaType.IMAGE && this.imageFormats[file.type]);
            // Do not select origin as first choice
            const isNotOrigin = file.type != MediaFileType.ORIGINAL;
            // console.log(this.allowedMediaTypes.SOUND ? 'SOUND' : 'IMAGE' + " - FIle match? ", file.type, fileTypeMatch, isInFormatList, isAllowed, isNotOrigin);
            return ((fileTypeMatch || isInFormatList) && isAllowed && isNotOrigin);
        });

        // Just select the first allowed type if no perfect match was found
        if(!this.activeFile) this.activeFile = this.media.files.find(file => this.allowedMediaTypes[file.mediaType] && file.type != MediaFileType.ORIGINAL);
    }

    private updateImageList() {
        if(!this.media) return this.imageFiles = [];
        // Filter out images to be shown in tab list (images that has a format)
        this.imageFiles = this.media.files.filter(file => (file.mediaType == MediaType.IMAGE && file.url && this.imageFormats[file.type]));
        this.shownImageFiles = this.imageFiles.filter(file => (file.url && !this.imageFormats[file.type].hideFile));
    }

    private getCropper(): UploadCropperComponent {
        if(this.externalCropper) return this.externalCropper;
        else return this.uploadCropper;
    }

    public async onFileSelect(file: MediaFile) {
        // Ensure all files have storage paths before uploading media
        this.updateStoragePath();
        // Mark this media as manually uploaded (not auto generated)
        this.media.isAutoGenerated = false;
        this.uploadMediaService.uploadMedia(this.media, file, this.getCropper(), this.imageFormats, this.allowedMediaTypes, this.containerOptions, this.setVideoImagePlaceholder);
    }

    public onCroppingModeChange(enabled: boolean) {
        this.croppingModeEnabled = enabled;
        // Indicate that a upload is pending (disable any save buttons)
        if(enabled) this.mediaUploadPending.emit(this.media);
    }

    /**
     * Enable cropping mode and upload cropped image when user has selected cropping area.
     * Service side optimize image with new cropping.
     */
    public async enableCroppingMode() {
        let activeFile = this.activeFile;
        this.activeFile = this.media.original;
        // Await user choosing cropping area
        let file = await this.uploadMediaService.cropImage(this.media, this.imageFormats, this.getCropper(), activeFile.type, true, this.roundCropper);

        this.activeFile = activeFile;
        // Cropping failed or was aborted
        if(!file) {
            this.mediaUploadDone.emit(this.media);
            return; // console.log("Cropping aborted", activeFile.type, activeFile.cropping, activeFile.rotation);
        }

        this.mediaUploadPending.emit(this.media);

        // Set rotation to the same on all files
        this.media.files.forEach(otherFile => otherFile.rotation = file.rotation);
        this.isUploadInProgress = true;
        // Mark this media as manually uploaded (not auto generated)
        this.media.isAutoGenerated = false;
        // Optimize cropped files
        this.uploadMediaService.sendUploadEvent(MediaUploadEventType.MEDIA_UPLOAD_DONE, this.media, file);
        // Optimize media if needed
        await this.uploadMediaService.optimizeMedia(this.media, this.media.original, this.allowedMediaTypes, this.containerOptions, this.imageFormats, file.mediaType, this.setVideoImagePlaceholder);
        this.activeFile = activeFile;
        this.isUploadInProgress = false;
    }

    public onCroppingError(err: string) {
        console.warn("Got cropping error ", err);
        this.isUploadInProgress = false;
        this.isCroppingInProgress = false;
        this.groupProgress.status = 'Error';
        this.groupProgress.totalPct = 0;
        this.toastService.addToast($localize`Sorry, something seems wrong with the media file, please try another file.`, ToastType.ERR);
    }

    public showFileTab(file: MediaFile) {
        this.activeFile = file;
    }

    public deletePressed() {
        this.showDeleteOverlay = true;
    }

    public async deleteConfirmed() {
        await this.uploadMediaService.deleteFiles(this.activeFile, this.media);
        // Select ned media type after delete
        this.uploadMediaService.updateMediaType(this.media);
        // Save delete changes
        await this.mediaService.updateMedia(this.media, this.containerOptions);
        // Select correct active file
        this.uploadMediaService.sendUpdateMediaTypeEvent(this.media);
        // Mark upload done to indicate files where deleted
        this.mediaUploadDone.emit(this.media);
        this.mediaDeleted.emit(this.media);
    }

    public addFile() {
        this.uploadComponents.first.openFileSelector();
    }

    public trackByFileType(index: number, file: MediaFile) {
        return file.type;
    }

    public copyMedia() {
        this.copyMediaRequest.emit(this.media);
    }

    public unlinkMedia() {
        if(this.mediaMoteLinks.length <= 1) return;

        let ok = confirm($localize`Unlink media from translations (make a copy)? This media is currently linked to ${this.mediaMoteLinks.length} other translations and these will no longer be affected by changes in this media.`);
        if(!ok) return;
        this.copyMedia();
    }

    public openMediaLibrary() {
        this.openLibraryRequest.emit();
    }

    private handleUploadEvent(ev: MediaUploadEvent) {
        switch(ev.type) {
            case MediaUploadEventType.UPLOAD_ACCEPTED:
                this.isUploadInProgress = true;
                // Notify parent that media is uploading
                this.mediaUploadPending.emit(ev.media);
                break;

            case MediaUploadEventType.DELETE_OLD_STARTED:
                // Mark progress as uploading while deleting old
                this.groupProgress.status = 'Downloading';
                this.groupProgress.totalPct = 0;
                break;

            case MediaUploadEventType.OLD_FILE_DELETED:
                // Update image list after delete
                if(ev.file.mediaType == MediaType.IMAGE) this.updateImageList();
                this.activateFirstFileTab();
                break;

            case MediaUploadEventType.IMAGE_UPLOAD_STARTED:
                // Set active image to hd
                this.activateFirstFileTab();
                // Update image file list
                this.updateImageList();
                break;

            case MediaUploadEventType.MEDIA_UPLOAD_STARTED:
                // Activate newly uploaded file (unless the media file uploaded is not allowed in
                // this instance of upload component, indicating that event should be handled in another instance)
                if(this.allowedMediaTypes[ev.file?.mediaType]) {
                    this.activeFile = ev.file;
                }
                break;

            case MediaUploadEventType.UPDATE_MEDIA_TYPE:
                this.uploadMediaService.updateMediaType(this.media);
                this.activateFirstFileTab();
                break;

            case MediaUploadEventType.MEDIA_UPLOAD_DONE:
                // Show delayed optimizing indicator on the component where file was uploaded
                if(this.allowedMediaTypes[ev.file?.mediaType]) {
                    this.isCroppingInProgress = true;
                }
                break;

            case MediaUploadEventType.MEDIA_CROPPING_DONE:
                // Emit media cropping done if the media event is related to this upload component
                if(this.allowedMediaTypes[ev.file?.mediaType]) this.mediaCroppingDone.emit();
                this.isUploadInProgress = false;
                this.isCroppingInProgress = false;
                // Apply updates from database that we received while uploading
                if(this.delayedMediaUpdate) this.media = this.delayedMediaUpdate;
                this.delayedMediaUpdate = null;
                break;
        }
    }

    public chooseVideoType(media: Media) {
        // Select the most appropriate video type
        if(media.video.url.includes("youtube.com")) {
            media.type = MediaType.YOUTUBE_VIDEO;
        }
        else if(media.video.url.includes("filmcentralen.dk")) {
            media.type = MediaType.FILMCENTRALEN_VIDEO;
        }
        else {
            media.type = MediaType.VIDEO;
        }
    }

    public chooseSoundType(media: Media) {
        // Prefer ambisonic if it exists else normal sound
        media.type = media.ambisonic.url ? MediaType.AMBISONIC : MediaType.SOUND;
    }

    public externalMediaHandler() {
        this.externalVideoPopupSubject.next();
    }

    public toggleMetaDataOverlay() {
        this.showMetaDataOverlay = !this.showMetaDataOverlay;
    }

    public resetForm() {
        this.showMetaDataOverlay = false;
        this.initMetaDataModels();
    }

    public saveMetaData() {
        this.showMetaDataOverlay = false;

        this.media.description = this.metaDescriptionModel;
        this.media.copyright = this.metaCopyrightModel;
        this.mediaService.updateMediaMetaData(this.media);
    }

    public externalVideoHandler() {
        this.activeFile = this.media.video;
        this.mediaService.updateMedia(this.media, this.containerOptions);
    }

    public downloadMedia() {
        switch(this.activeFile.mediaType) {
            case (MediaType.IMAGE): {
                return this.downloadFile(this.media.original.url);
            }
            case (MediaType.VIDEO):
            case (MediaType.SOUND):
            case (MediaType.AMBISONIC): {
                return this.downloadFile(this.activeFile.url);
            }
            case (MediaType.FILMCENTRALEN_VIDEO || MediaType.YOUTUBE_VIDEO): {
                console.log('Can not download external videos');
                return;
            }
            default: {
                console.error('Download Error: Unknown mediatype', this.activeFile.mediaType);
            }
        }
    }

    public downloadFile(fileUrl: string) {
        this.httpClient.get(fileUrl, { responseType: 'blob' }).subscribe((blob) => {
            // Get extension from MIME type
            const ext = blob.type.split('/')[1];
            saveAs(blob, `storyhunt_media_${(new Date()).toISOString()}.${ext}`);
        });
    }

    public trackByMediaFileKey(index: number, file: MediaFile): string {
        return file.mediaRef.key + "_" + file.type;
    }

    public enableEditMode() {
        switch(this.activeFile.type) {
            case (MediaFileType.HD):
            case (MediaFileType.PREVIEW):
            case (MediaFileType.SQUARE):
            case (MediaFileType.THUMB): {
                this.enableCroppingMode();
                break;
            }
            case (MediaFileType.VIDEO): {
                this.isVideoEditEnabled = true;
                this.showVideoCover = false;
                break;
            }
            default: {
                console.error(`Invalid type when selecting edit mode: ${this.activeFile.type}`);
            }
        }
    }

    public async generateScreenshot() {
        this.isVideoEditEnabled = false;
        if (this.media.original) {
            await this.uploadMediaService.deleteFiles(this.media.original, this.media);
            await this.mediaService.updateMedia(this.media, this.containerOptions);
        }
        await this.uploadMediaService.generateScreenshot(this.media, this.containerOptions, this.videoPlayerTimeInMs);
    }

    public updateVideoPlaceholderImage() {
        this.isVideoEditEnabled = false;
        if(this.media.type != MediaType.VIDEO) {
            console.error('Can not set video placeholder on non video media');
            return;
        }
        this.addFile();
    }
}
